/**
 * RD-1 (C5) — local-first county/state resolution from the `tiger-counties`
 * refdata snapshot (Census TIGER county polygons, point containment).
 *
 * Replaces the `geo.fcc.gov/api/census/block/find` dependency at the call
 * sites that only need state/county identity (epa-attainment,
 * eia-electricity, eia-natural-gas, regulatory-db). NOT used by
 * jurisdiction.ts: its `ResolvedJurisdictionFips` carries FCC block-level
 * fields (blockFips/blockBbox) that persist into the SED and cannot be
 * derived from county polygons — that provider keeps the FCC path.
 *
 * Returns null when the dataset is unavailable OR no county contains the
 * point (offshore) — callers fall through to their existing FCC logic.
 */

import { refdataContains, tryRefdata } from './refdataClient';
import type { RefdataFeature } from '../../types/refdata';

export interface RefdataFipsResult {
  /** 5-digit county GEOID (state + county). */
  countyFips: string;
  stateFips: string;
  countyName: string;
  /** USPS state/territory code, e.g. 'CA'. */
  stateCode: string;
}

/** State/territory FIPS → USPS postal code (fixed by the Census Bureau). */
const STATE_FIPS_TO_POSTAL: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY', '60': 'AS', '66': 'GU', '69': 'MP', '72': 'PR', '78': 'VI',
};

export function statePostalCodeForFips(stateFips: string): string | null {
  return /^\d{2}$/.test(stateFips) ? (STATE_FIPS_TO_POSTAL[stateFips] ?? null) : null;
}

function countyToResult(county: RefdataFeature): RefdataFipsResult | null {
  const geoid = typeof county.props.GEOID === 'string' ? county.props.GEOID
    : typeof county.props.GEOID === 'number' ? String(county.props.GEOID).padStart(5, '0') : '';
  if (!/^\d{5}$/.test(geoid)) return null;
  const stateFips = geoid.slice(0, 2);
  const stateCode = STATE_FIPS_TO_POSTAL[stateFips];
  const rawName = county.props.NAME ?? county.props.BASENAME;
  const countyName = typeof rawName === 'string' ? rawName.trim() : '';
  if (!stateCode || countyName === '') return null;
  return { countyFips: geoid, stateFips, countyName, stateCode };
}

export async function resolveFipsFromRefdata(lat: number, lng: number): Promise<RefdataFipsResult | null> {
  // Wrapper object so an authoritative "no county here" (offshore) is NOT
  // negative-cached as dataset-unavailable by tryRefdata.
  const screened = await tryRefdata('tiger-counties', async () => {
    const res = await refdataContains('tiger-counties', { lat, lng });
    if (!res) return null;
    return { county: res.features[0] ?? null };
  });
  if (!screened?.county) return null;
  return countyToResult(screened.county);
}
